import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, Calendar, Download } from 'lucide-react';
import StoreLayout from '../../components/store/StoreLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { toast } from 'sonner';

export default function QuizResultsPage() {
  const { user } = useAuth();
  const [quizResults, setQuizResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadQuizResults();
    }
  }, [user]);

  const loadQuizResults = async () => {
    try {
      setLoading(true);
      const response = await api.get('/leads/my-quizzes');
      setQuizResults(response.data?.items || []);
    } catch (error) {
      console.error('Error loading quiz results:', error);
      toast.error('Erreur lors du chargement des résultats');
    } finally {
      setLoading(false);
    }
  };

  const getStyleBadge = (style) => {
    const styleColors = {
      casual: 'bg-blue-100 text-blue-800',
      elegant: 'bg-purple-100 text-purple-800',
      classic: 'bg-gray-100 text-gray-800',
      trendy: 'bg-pink-100 text-pink-800',
      sporty: 'bg-green-100 text-green-800',
    };
    
    return (
      <Badge className={styleColors[style?.toLowerCase()] || 'bg-gray-100 text-gray-800'}>
        {style}
      </Badge>
    );
  };

  if (!user) {
    return (
      <StoreLayout>
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md mx-auto text-center">
            <FileText className="h-16 w-16 text-muted-foreground/50 mx-auto mb-6" />
            <h1 className="font-display text-3xl font-bold mb-4">Mes résultats de quiz</h1>
            <p className="text-muted-foreground mb-8">
              Connectez-vous pour voir vos résultats de quiz
            </p>
            <Button size="lg" asChild>
              <Link to="/login">Se connecter</Link>
            </Button>
          </div>
        </section>
      </StoreLayout>
    );
  }

  if (loading) {
    return (
      <StoreLayout>
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          </div>
        </section>
      </StoreLayout>
    );
  }

  return (
    <StoreLayout>
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-display text-4xl font-bold mb-2">Mes résultats de quiz</h1>
                <p className="text-muted-foreground">
                  Retrouvez tous vos profils style et recommandations personnalisées
                </p>
              </div>
              <Button variant="outline" asChild>
                <Link to="/account/settings">Retour</Link>
              </Button>
            </div>

            {/* Quiz Results */}
            {quizResults.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <FileText className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4" />
                  <h3 className="font-medium text-lg mb-2">Aucun quiz complété</h3>
                  <p className="text-muted-foreground mb-6">
                    Complétez notre quiz style pour découvrir votre profil et recevoir des recommandations personnalisées
                  </p>
                  <Button asChild>
                    <Link to="/quiz">Faire le quiz style</Link>
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {quizResults.map((result, index) => (
                  <motion.div
                    key={result.id || index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle className="flex items-center gap-2">
                            <FileText className="h-5 w-5" />
                            Quiz Style #{index + 1}
                          </CardTitle>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Calendar className="h-4 w-4" />
                            {new Date(result.created_at).toLocaleDateString('fr-FR')}
                          </div>
                        </div>
                        <CardDescription>
                          Votre profil style et préférences
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {result.quiz_results && (
                          <>
                            {/* Style Profile */}
                            {result.quiz_results.style && (
                              <div>
                                <h4 className="font-medium mb-2">Votre style</h4>
                                <div className="flex flex-wrap gap-2">
                                  {Array.isArray(result.quiz_results.style) ? (
                                    result.quiz_results.style.map((style, idx) => (
                                      <div key={idx}>{getStyleBadge(style)}</div>
                                    ))
                                  ) : (
                                    getStyleBadge(result.quiz_results.style)
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Occasions */}
                            {result.quiz_results.occasions && (
                              <div>
                                <h4 className="font-medium mb-2">Occasions préférées</h4>
                                <div className="flex flex-wrap gap-2">
                                  {(Array.isArray(result.quiz_results.occasions)
                                    ? result.quiz_results.occasions
                                    : [result.quiz_results.occasions]
                                  ).map((occasion, idx) => (
                                    <Badge key={idx} variant="outline">
                                      {occasion}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Colors */}
                            {result.quiz_results.colors && (
                              <div>
                                <h4 className="font-medium mb-2">Couleurs favorites</h4>
                                <div className="flex flex-wrap gap-2">
                                  {(Array.isArray(result.quiz_results.colors)
                                    ? result.quiz_results.colors
                                    : [result.quiz_results.colors]
                                  ).map((color, idx) => (
                                    <Badge key={idx} variant="secondary">
                                      {color}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Budget */}
                            {result.quiz_results.budget && (
                              <div>
                                <h4 className="font-medium mb-2">Budget</h4>
                                <Badge variant="outline">{result.quiz_results.budget}</Badge>
                              </div>
                            )}

                            {/* Size */}
                            {result.quiz_results.size && (
                              <div>
                                <h4 className="font-medium mb-2">Taille</h4>
                                <Badge variant="outline">{result.quiz_results.size}</Badge>
                              </div>
                            )}

                            {/* Full Results JSON (collapsible) */}
                            <details className="mt-4">
                              <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                                Voir les détails complets
                              </summary>
                              <pre className="mt-2 p-4 bg-muted rounded text-xs overflow-auto">
                                {JSON.stringify(result.quiz_results, null, 2)}
                              </pre>
                            </details>
                          </>
                        )}

                        {/* Actions */}
                        <div className="flex gap-2 pt-4 border-t">
                          <Button variant="outline" size="sm" asChild>
                            <Link to="/looks">
                              Voir les looks recommandés
                            </Link>
                          </Button>
                          <Button variant="outline" size="sm" asChild>
                            <Link to="/quiz">
                              Refaire le quiz
                            </Link>
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </section>
    </StoreLayout>
  );
}
